import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Sun,
} from "lucide-react";

/**
 * Pure display component. All weather data is fetched server-side at ride
 * create/update time and stored in the ride document. No API calls here.
 *
 * Expected shape of `weather` prop:
 *   { min_c, max_c, code, place, fetched_at, error }
 * When `error` is set OR data is missing, we show "Clima indisponível" gracefully.
 */

function describe(code) {
  if (code === 0) return { label: "Céu limpo", Icon: Sun, color: "#FBBF24" };
  if (code <= 3) return { label: "Parcialmente nublado", Icon: CloudSun, color: "#93C5FD" };
  if (code === 45 || code === 48) return { label: "Nevoeiro", Icon: CloudFog, color: "#94A3B8" };
  if (code >= 51 && code <= 57) return { label: "Garoa", Icon: CloudRain, color: "#60A5FA" };
  if (code >= 61 && code <= 67) return { label: "Chuva", Icon: CloudRain, color: "#3B82F6" };
  if (code >= 71 && code <= 77) return { label: "Neve", Icon: CloudSnow, color: "#E0F2FE" };
  if (code >= 80 && code <= 82) return { label: "Pancadas de chuva", Icon: CloudRain, color: "#2563EB" };
  if (code >= 85 && code <= 86) return { label: "Pancadas de neve", Icon: CloudSnow, color: "#E0F2FE" };
  if (code >= 95) return { label: "Trovoada", Icon: CloudLightning, color: "#F59E0B" };
  return { label: "Previsão indisponível", Icon: Cloud, color: "#94A3B8" };
}

export default function WeatherStrip({ weather }) {
  // Missing / errored / incomplete: friendly fallback (still keeps layout stable)
  const hasData =
    weather &&
    !weather.error &&
    weather.min_c != null &&
    weather.max_c != null &&
    weather.code != null;

  if (!hasData) {
    // Silent hide when there's no weather at all (e.g., past ride, never fetched)
    if (!weather) return null;
    return (
      <div
        className="mt-4 border border-white/10 bg-[#0B0D14] px-4 py-3 flex items-center gap-3 text-white/50 text-xs"
        data-testid="weather-error"
      >
        <Cloud size={14} />
        <span>Clima indisponível</span>
      </div>
    );
  }

  const { min_c, max_c, code, place } = weather;
  const { Icon, label, color } = describe(code);

  return (
    <div
      className="mt-4 border border-white/10 bg-[#0B0D14] px-4 py-3 flex items-center gap-4 flex-wrap"
      data-testid="weather-strip"
    >
      <div className="flex items-center gap-3 shrink-0">
        <Icon size={26} style={{ color }} aria-hidden="true" />
        <div className="min-w-0">
          <div className="text-white font-semibold text-sm leading-tight">
            {Math.round(min_c)}° / {Math.round(max_c)}°
          </div>
          <div className="text-white/60 text-xs mt-0.5">{label}</div>
        </div>
      </div>
      {place && (
        <div className="label-caps text-white/40 text-[10px] truncate">
          {place}
        </div>
      )}
      <div className="label-caps text-white/30 text-[10px] ml-auto">
        <a
          href="https://open-meteo.com/"
          target="_blank"
          rel="noreferrer"
          className="hover:text-white/60"
        >
          Open-Meteo
        </a>
      </div>
    </div>
  );
}
